import { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router';
import TimeAgo from 'react-timeago';
import { formatDuration } from './formatters';

/**
 * Renders a timestamp as a relative time (e.g., "5 minutes ago").
 *
 * react-timeago already sets a `title` attribute on the rendered <time>
 * element, so hovering shows the absolute timestamp (the raw ISO value we
 * pass in) for free — handy for copying into log queries like Datadog.
 */
export function TimeAgoCellRenderer({ value }: { value: string | null }) {
  if (!value) return <span>-</span>;
  return <TimeAgo date={value} />;
}

/**
 * Renders a number with locale-specific formatting.
 */
export function NumberCellRenderer({ value }: { value: number | null }) {
  if (value === null || value === undefined) return <span>-</span>;
  return <span>{value.toLocaleString()}</span>;
}

/**
 * Renders a duration in seconds as a human-readable format.
 */
export function DurationCellRenderer({ value }: { value: number | null }) {
  return <span>{formatDuration(value)}</span>;
}

/**
 * Links a sample row to its execution-timeline page. stopPropagation keeps the
 * row's own click (which opens the transcript viewer) from also firing.
 */
export function TimelineLinkCellRenderer({
  data,
}: {
  data?: { eval_set_id?: string; uuid?: string };
}) {
  if (!data?.eval_set_id || !data?.uuid) return <span>-</span>;
  const to = `/eval-set/${encodeURIComponent(data.eval_set_id)}/sample/${encodeURIComponent(data.uuid)}/timeline`;
  return (
    <Link
      to={to}
      // The grid's row/cell handlers (which open the transcript) skip link
      // clicks via isLinkClick, so the link's own navigation wins for plain,
      // ctrl, meta, and middle clicks alike.
      onClick={e => e.stopPropagation()}
      className="text-brand-700 hover:text-brand-600 dark:text-brand-300 dark:hover:text-brand-200"
    >
      Timeline
    </Link>
  );
}

/**
 * Small copy-to-clipboard button that shows a checkmark on success.
 */
export function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!navigator.clipboard?.writeText) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      navigator.clipboard.writeText(value).then(
        () => {
          setCopied(true);
          timerRef.current = setTimeout(() => setCopied(false), 1500);
        },
        err => {
          console.error('Failed to copy to clipboard:', err);
        }
      );
    },
    [value]
  );

  return (
    <button
      type="button"
      className="copy-button-cell-btn"
      onClick={handleCopy}
      title="Copy to clipboard"
      aria-label="Copy to clipboard"
    >
      {copied ? (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  );
}

/**
 * Renders a value with a small copy-to-clipboard button that appears on hover.
 */
export function CopyButtonCellRenderer({
  value,
  displayValue,
}: {
  value: string | null;
  displayValue?: string;
}) {
  if (!value) return <span>-</span>;

  return (
    <span className="copy-button-cell">
      <span className="copy-button-cell-text" title={value}>
        {displayValue ?? value}
      </span>
      <CopyButton value={value} />
    </span>
  );
}
